import React, { useState, useEffect } from 'react'
import { FormPartInterface } from '../../../interfaces'
import { FieldGroup, FormCard, SubmitButton, Field } from '../../reusable'
import { Checkbox } from '../../reusable/checkbox'
import { useClaimData, useUpdateClaimData } from '../../../claimProvider'
import { sectionValidation } from '../../../helpers'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { Loading } from 'notiflix'
import styles from './styles.module.scss'
import responsiveStyles from './responsiveStyles.module.scss'

export const ConfirmDetails = (props: FormPartInterface) => {
    const { isActive, assignGroup, saveValueFunct } = props
    const [isMarketingConsentConfirmed, setIsMarketingConsentConfirmed] = useState(false)
    const [isPolicyConsentConfirmed, setIsPolicyConsentConfirmed] = useState(false)
    const [isDisclosureConfirmed, setIsDisclosureConfirmed] = useState(false)
    const [stepData, setStepData] = useState<any>({})
    const [currentStep, setCurrentStep] = useState(0)
    const [postCurrentStep, setPostCurrentStep] = useState(0)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const claimData = useClaimData()
    const updateClaim = useUpdateClaimData()

    useEffect(() => {
        // Check existing consent values
        // console.log('claimData', claimData)

        if (claimData.marketing_consent === 'yes') {
            setIsMarketingConsentConfirmed(true)
        }
        if (claimData.policy_consent === 'yes') {
            setIsPolicyConsentConfirmed(true)
        }
        if (claimData.disclosure_consent === 'yes') {
            setIsDisclosureConfirmed(true)
        }

        setStepData(claimData)
    }, [claimData])

    useEffect(() => {
        if (isActive === 'signature' && isSubmitting) {
            Loading.remove()
            setIsSubmitting(false)
        }
    }, [isActive, isSubmitting])

    const handleProceed = async () => {
        try {
            if (!isDisclosureConfirmed) {
                Report.failure(
                    'Error',
                    'You must confirm you have read and understand the information about free routes and your choices to proceed.',
                    'OK',
                )
                return
            }
            if (!isPolicyConsentConfirmed) {
                Report.failure('Error', 'You must agree to the Privacy Policy and Terms & Conditions to proceed.', 'OK')
                return
            }

            Loading.standard('Processing your submission...')
            setIsSubmitting(true)

            let alternateAddressesData = null
            const previousAddresses = formatPreviousAddresses()

            if (previousAddresses && previousAddresses.length > 0) {
                alternateAddressesData = previousAddresses.map((address: any) => ({
                    client_id: claimData.client_id,
                    house_number: '',
                    house_name: '',
                    flat: '',
                    street: '',
                    town_city: address.town || '',
                    county: address.county || '',
                    postcode: address.postcode || '',
                    address_line_one: address.line_one || '',
                    address_line_two: address.line_two || '',
                    address_line_three: address.line_three || '',
                    raw_address: address.text || '',
                }))
            } else {
                console.log('No previous addresses provided by user')
            }

            const updatedData = {
                ...claimData,
                ...stepData,
                policy_consent: 'yes',
                disclosure_consent: 'yes',
                alternate_addresses: alternateAddressesData,
            }

            updateClaim(updatedData)
            assignGroup('signature')
        } catch (error: any) {
            Loading.remove()
            setIsSubmitting(false)
            Report.failure('Error', error.message, 'OK')
        }
    }

    const formatAddress = () => {
        const addressParts = [
            claimData.address_line_one,
            claimData.address_line_two,
            claimData.address_line_three,
            claimData.city,
            claimData.postcode,
        ].filter(Boolean)

        return (
            <>
                {addressParts.map((part, index) => (
                    <React.Fragment key={index}>
                        {part}
                        {index < addressParts.length - 1 && <br />}
                    </React.Fragment>
                ))}
            </>
        )
    }

    const formatPreviousAddresses = () => {
        try {
            const previousAddressesData = claimData.previous_addresses
            if (!previousAddressesData) return null

            let addresses = []
            if (typeof previousAddressesData === 'string') {
                addresses = JSON.parse(previousAddressesData)
            } else if (Array.isArray(previousAddressesData)) {
                addresses = previousAddressesData
            }

            if (!addresses || addresses.length === 0) return null

            return addresses
        } catch (error) {
            console.error('Error parsing previous addresses:', error)
            return null
        }
    }

    const getCurrentStep = () => {
        return isActive === 'confirm' ? (
            <FieldGroup title="Confirm Your Details">
                <FormCard step={currentStep} assignPostStep={setPostCurrentStep} onBack={() => assignGroup('contact')}>
                    <h3>Please Review Your Details</h3>
                    <p>Please check that all the information below is correct before proceeding.</p>
                    <div className={`${styles.card} ${responsiveStyles.card} irresponsibe-lending-confirm`}>
                        <div className={`${styles.section} ${responsiveStyles.section}`}>
                            <h4 className={`${styles.sectionHeader} ${responsiveStyles.sectionHeader}`}>
                                Personal Information
                            </h4>

                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>Title:</span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {claimData.title || 'Not provided'}
                                </span>
                            </div>

                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>
                                    First Name:
                                </span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {claimData.firstname || 'Not provided'}
                                </span>
                            </div>

                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>
                                    Last Name:
                                </span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {claimData.lastname || 'Not provided'}
                                </span>
                            </div>

                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>
                                    Date of Birth:
                                </span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {stepData.dob || 'Not provided'}
                                </span>
                            </div>
                        </div>

                        <hr className={`${styles.divider} ${responsiveStyles.divider}`} />

                        <div className={`${styles.section} ${responsiveStyles.section}`}>
                            <h4 className={`${styles.sectionHeader} ${responsiveStyles.sectionHeader}`}>
                                Current Address
                            </h4>
                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>Address:</span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {formatAddress()}
                                </span>
                            </div>
                        </div>

                        {formatPreviousAddresses() && (
                            <>
                                <hr className={`${styles.divider} ${responsiveStyles.divider}`} />
                                <div className={`${styles.section} ${responsiveStyles.section}`}>
                                    <h4 className={`${styles.sectionHeader} ${responsiveStyles.sectionHeader}`}>
                                        Previous Addresses (Last 3 Years)
                                    </h4>
                                    {formatPreviousAddresses()?.map((address: any, index: number) => (
                                        <div
                                            key={index}
                                            className={`${styles.field} ${responsiveStyles.field}`}
                                            style={{ marginBottom: '15px' }}
                                        >
                                            <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>
                                                Address {index + 1}:
                                            </span>
                                            <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                                {address.line_one}
                                                {address.line_two && (
                                                    <>
                                                        <br />
                                                        {address.line_two}
                                                    </>
                                                )}
                                                {address.line_three && (
                                                    <>
                                                        <br />
                                                        {address.line_three}
                                                    </>
                                                )}
                                                <br />
                                                {address.town}
                                                {address.county && `, ${address.county}`}
                                                <br />
                                                {address.postcode}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                        <hr className={`${styles.divider} ${responsiveStyles.divider}`} />

                        <div className={`${styles.section} ${responsiveStyles.section}`}>
                            <h4 className={`${styles.sectionHeader} ${responsiveStyles.sectionHeader}`}>
                                Contact Information
                            </h4>
                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>Email:</span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {claimData.email || 'Not provided'}
                                </span>
                            </div>

                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>Phone:</span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {claimData.telephone || 'Not provided'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className={`${styles.disclosure} ${responsiveStyles.disclosure || ''}`}>
                        <p className={styles.disclosureIntro}>
                            Before instructing Sentinel Legal in relation to a motor finance claim, please read the
                            following.
                        </p>

                        <h4 className={styles.disclosureHeading}>Free Routes Are Available</h4>
                        <p>
                            You can complain directly to your lender at no cost, and escalate to the Financial
                            Ombudsman Service (FOS) free of charge if you are not satisfied with the response.
                        </p>
                        <p>
                            The Financial Conduct Authority (FCA) has also announced a Motor Finance Redress Scheme,
                            which eligible consumers will be able to use to obtain compensation without instructing a
                            solicitor and without paying any fees.
                        </p>
                        <p>
                            You do not need to instruct Sentinel Legal, or any solicitor or claims management company,
                            to access these free routes.
                        </p>

                        <h4 className={styles.disclosureHeading}>Why Some Clients Still Choose Litigation</h4>
                        <p>
                            In our view, based on cases the firm has seen and outcomes recorded in litigation to date,
                            the compensation produced by the FCA&apos;s proposed redress methodology may be materially
                            lower than awards available through the courts. The FCA&apos;s proposals also remain
                            subject to consultation and challenge, and the position may change before the scheme is
                            finalised.
                        </p>
                        <p>
                            Litigation carries different risks, timescales and costs to the FCA scheme, and the firm
                            will explain those in full before you instruct us. Past outcomes are not a guarantee of
                            future results.
                        </p>

                        <h4 className={styles.disclosureHeading}>Your Choice</h4>
                        <p>You may:</p>
                        <ul className={styles.disclosureList}>
                            <li>Use the FCA Motor Finance Redress Scheme at no cost.</li>
                            <li>Complain to your lender directly at no cost.</li>
                            <li>Refer your complaint to the Financial Ombudsman Service at no cost.</li>
                            <li>Instruct Sentinel Legal on a no win, no fee basis.</li>
                        </ul>
                        <p>
                            If you instruct Sentinel Legal, you will receive a client care letter setting out our fees,
                            your cancellation rights, and the alternative routes above before you sign any retainer.
                        </p>
                    </div>

                    <Field fieldLabel="">
                        <Checkbox
                            fieldName="disclosure_consent"
                            label={<>I have read and understand the information above.</>}
                            initialValue={claimData.disclosure_consent}
                            saveValue={(data) => {
                                setIsDisclosureConfirmed(data.value === 'yes')
                                return saveValueFunct(data)
                            }}
                        />
                    </Field>

                    <Field fieldLabel="">
                        <Checkbox
                            fieldName="marketing_consent"
                            label={
                                <>
                                    I consent to receiving updates, offers, and information from Sentinel Legal about
                                    related legal services and promotions via email, phone, or SMS. I understand I can
                                    opt out at any time by contacting Sentinel Legal or using the unsubscribe link in
                                    any message.
                                </>
                            }
                            initialValue={claimData.marketing_consent}
                            saveValue={(data) => {
                                setIsMarketingConsentConfirmed(data.value === 'yes')
                                return saveValueFunct(data)
                            }}
                        />
                    </Field>

                    <Field fieldLabel="">
                        <Checkbox
                            fieldName="policy_consent"
                            label={
                                <>
                                    By submitting this form, you agree to our{' '}
                                    <a
                                        href="https://www.sentinellegal.co.uk/privacy-policy"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.policyLink}
                                    >
                                        Privacy Policy
                                    </a>{' '}
                                    and{' '}
                                    <a
                                        href="https://www.sentinellegal.co.uk/terms-conditions"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.policyLink}
                                    >
                                        Terms & Conditions
                                    </a>
                                    . These explain how we use, store, and protect your data.
                                </>
                            }
                            initialValue={claimData.policy_consent}
                            saveValue={(data) => {
                                setIsPolicyConsentConfirmed(data.value === 'yes')
                                return saveValueFunct(data)
                            }}
                        />
                    </Field>

                    <SubmitButton
                        text="Proceed to Signature"
                        btnType="primary"
                        onClick={handleProceed}
                        disabled={!isPolicyConsentConfirmed || !isDisclosureConfirmed}
                    />
                </FormCard>
            </FieldGroup>
        ) : (
            <></>
        )
    }

    return getCurrentStep()
}
